import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { toast } from 'react-toastify';
import { z } from 'zod';

import { handleUnexpectedError } from '@/app/errors';
import { useCurrentUser } from '@/app/hooks/use-current-user';
import { useAppForm } from '@/app/lib/tanstack-form';
import { saveAccountAlias } from '@/app/network/firefly-api';
import { Button, ButtonGroup } from '@/common/components/button';
import { Field, FieldInfo, Label } from '@/common/components/fieldset';
import { Heading } from '@/common/components/heading';
import { Text } from '@/common/components/text';
import { getAccountLabel } from '@/shared/accounts';
import { Toast } from '@/shared/components/toast';
import { getCurrentUserQueryOptions } from '@/shared/user/queries';

const accountAliasesFormSchema = z.object({
  accounts: z.array(z.object({
    accountNumber: z.number(),
    alias: z.string().max(100, 'Nickname cannot be longer than 100 characters.')
  }))
});

export const Route = createFileRoute('/wizard/memberships/$member-number/aliases')({
  component: AliasesPage
});

function AliasesPage() {
  const { membership } = Route.useRouteContext();
  const { accounts, id } = useCurrentUser();
  const membershipAccounts = accounts.filter(account => account.member_number === membership.member_number);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const accountsMap = new Map(membershipAccounts.map(account => [account.account_number, account]));

  const { mutateAsync } = useMutation({
    mutationFn: async (values: z.infer<typeof accountAliasesFormSchema>['accounts']) => {
      await Promise.all(values.map(value => saveAccountAlias(id, value.accountNumber, value.alias)));
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: getCurrentUserQueryOptions()['queryKey'] });
      toast.success(Toast, { data: { title: 'Your Account Nicknames have been saved.' } });
      await navigate({ to: '/wizard/memberships/$member-number/notifications', params: { 'member-number': membership.member_number.toString() } });
    },
    onError: error => handleUnexpectedError(error)
  });

  const form = useAppForm({
    defaultValues: {
      accounts: membershipAccounts.map(account => ({
        accountNumber: account.account_number,
        alias: account.alias ?? ''
      }))
    },
    validators: {
      onBlur: accountAliasesFormSchema
    },
    onSubmit: async ({ value }) => {
      await mutateAsync(value.accounts);
    }
  });

  return (
    <>
      <Heading>Account Nicknames</Heading>
      <Text>You may edit nicknames for your linked accounts below.</Text>

      {membershipAccounts.length === 0 && (
        <div className="flex flex-col mt-6">
          <p>No accounts are associated with this membership.</p>
          <ButtonGroup>
            <Button to="/wizard/memberships/$member-number/notifications" params={{ 'member-number': membership.member_number.toString() }}>
              Continue
            </Button>
          </ButtonGroup>
        </div>
      )}

      {membershipAccounts.length > 0 && (
        <form
          className="grid w-full grid-cols-1 gap-8"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <form.AppField name="accounts" mode="array">
            {field => (
              field.state.value.map((accountValue, idx) => {
                const account = accountsMap.get(accountValue.accountNumber)!;

                return (
                  <Field key={idx}>
                    <Label>{getAccountLabel(account, true)}</Label>
                    <form.AppField name={`accounts[${idx}].alias`}>
                      {(subField) => {
                        return (
                          <>
                            <subField.FormInput />
                            <FieldInfo field={subField} />
                          </>
                        );
                      }}
                    </form.AppField>
                  </Field>
                );
              }))}
          </form.AppField>
          <ButtonGroup>
            <Button type="submit">Continue</Button>
          </ButtonGroup>
        </form>
      )}
    </>
  );
}
